import { Image } from '#app/components/Image/Image.js';
import { Text } from '#app/components/Primitives/Text.js';
import { type Sortable } from '#app/components/SortableListView/types.js';
import { Tooltip } from '#app/components/Tooltip/Tooltip.js';
import { type Theme } from '#app/theme/index.js';
import { dashedBorderBackground } from '#app/utilities/styles.js';
import {
  AddIcon,
  CheckIcon,
  ChevronDownIcon,
  CloseIcon,
  MoveIcon,
} from '@contra/icons/icons';
import {
  forwardRef,
  type KeyboardEvent,
  type MouseEvent,
  type Ref,
} from 'react';
import { css, keyframes, styled } from 'styled-components';

export const DESKTOP_TAG_HEIGHT = 40;
export const MOBILE_TAG_HEIGHT = 32;
const SMALL_TAG_SIZE = 24;
const MEDIUM_TAG_SIZE = 32;
const MOBILE_TAG_HEIGHT_LARGE = 40;
const DESKTOP_ICON_SIZE = 24;
const MOBILE_ICON_SIZE = 18;
const SMALL_ICON_SIZE = 16;
const MOBILE_ICON_SIZE_LARGE = 24;

const popIn = keyframes`
  0% {
    opacity: 0;
  }

  100% {
    opacity: 0.7;
  }
`;

const getTagActionIcon = (mode: TagMode) => {
  if (mode === 'none') {
    return null;
  }

  if (mode === 'add') {
    return AddIcon;
  }

  if (mode === 'select') {
    return ChevronDownIcon;
  }

  if (mode === 'checked') {
    return CheckIcon;
  }

  return CloseIcon;
};

const getMobileIconSize = ({
  size,
  variant,
}: {
  size: TagProps['size'];
  variant?: TagVariant;
}) => {
  if (size === 'small') {
    return SMALL_ICON_SIZE;
  }

  return variant === 'whiteMobileLarger'
    ? MOBILE_ICON_SIZE_LARGE
    : MOBILE_ICON_SIZE;
};

const getIconElement = ({
  icon,
  size,
  variant,
}: Pick<TagProps, 'icon' | 'size' | 'variant'>) => {
  const mobileIconSize = getMobileIconSize({
    size,
    variant,
  });

  return icon && 'uid' in icon ? (
    <Image
      height={mobileIconSize}
      uid={icon.uid}
      width={mobileIconSize}
    />
  ) : (
    icon
  );
};

const getTagVariantStyles = (
  theme: Theme,
  variant: TagVariant,
  $readOnly: boolean,
  $showHoverState: boolean,
) => {
  const cursor = $readOnly ? css`default` : css`pointer`;
  switch (variant) {
    case 'blue': {
      return css`
        background-color: ${theme.colorsV2.blue05};
        color: ${theme.colorsV2.blue80};
        border: 1px solid ${theme.colorsV2.blue05};
        opacity: 1;
        transition: opacity 175ms ease;
        animation: ${popIn} 125ms ease;

        & > svg {
          margin-left: 8px;
        }

        ${!$readOnly &&
        css`
          &:hover,
          &:focus,
          &:focus-within,
          &:active {
            cursor: ${cursor};
            opacity: 1;
            border: 1px solid ${theme.colorsV2.blue80};
          }
        `}
      `;
    }

    case 'blueSemiBold': {
      return css`
        background-color: ${theme.colorsV2.blue05};
        color: ${theme.colorsV2.blue70};
        border: 1px solid ${theme.colorsV2.blue05};
        opacity: 1;
        transition: opacity 175ms ease;
        animation: ${popIn} 125ms ease;
        height: ${MOBILE_TAG_HEIGHT}px;
        border-radius: ${MOBILE_TAG_HEIGHT / 2}px;

        & > svg {
          margin-left: 8px;
        }
      `;
    }

    case 'condensed': {
      return css`
        background-color: ${theme.colorsV2.white};
        border: 1px solid ${theme.colorsV2.gray30};
        padding: 8px 10px;
      `;
    }

    case 'condensed-link': {
      return css`
        background-color: ${theme.colorsV2.white};
        border: 1px solid ${theme.colorsV2.gray30};

        padding: 8px 10px;
        ${!$readOnly &&
        css`
          &:hover {
            border: 1px solid ${theme.colorsV2.gray40};
            background-color: ${theme.colorsV2.gray10};
          }
        `}
      `;
    }

    case 'dotted': {
      return css`
        background-color: ${theme.colorsV2.white};
        color: ${theme.colorsV2.black};
        border: 1px solid ${theme.colorsV2.gray30};
        opacity: 1;
        transition: opacity 175ms ease;
        animation: ${popIn} 125ms ease;

        & > svg {
          margin-left: 8px;
        }

        border: 1px dashed ${theme.colorsV2.gray30};
      `;
    }

    case 'ghost': {
      return css`
        background-color: ${theme.colorsV2.gray05};
        border: 1px solid ${theme.colorsV2.gray30};
      `;
    }

    case 'gray': {
      return css`
        background-color: ${theme.colorsV2.gray05};
      `;
    }

    case 'gray10': {
      return css`
        background-color: ${theme.colorsV2.gray10};

        transition: border-color 0.13s ease-out;

        ${$showHoverState &&
        css`
          cursor: pointer;
          border: 1px solid transparent;

          &:hover {
            border: 1px solid ${theme.colorsV2.gray30};
          }
        `}
      `;
    }

    case 'gray20': {
      return css`
        background-color: ${theme.colorsV2.gray20};

        transition: border-color 0.13s ease-out;

        ${$showHoverState &&
        css`
          cursor: pointer;
          border: 1px solid transparent;

          &:hover {
            border: 1px solid ${theme.colorsV2.gray40};
          }
        `}
      `;
    }

    case 'gray90': {
      return css`
        color: ${theme.colorsV2.white};
        background-color: ${theme.colorsV2.gray90};
      `;
    }

    case 'gray-condensed': {
      return css`
        background-color: ${theme.colorsV2.gray10};
        color: ${theme.colorsV2.gray60};
        padding: 8px 10px;
        transition: border-color 0.13s ease-out;

        ${$showHoverState &&
        css`
          cursor: pointer;
          border: 1px solid transparent;

          &:hover {
            border: 1px solid ${theme.colorsV2.gray40};
          }
        `}
      `;
    }

    case 'green': {
      return css`
        background-color: ${theme.colorsV2.green05};
        color: ${theme.colorsV2.green80};
        opacity: 1;
        transition: opacity 175ms ease;
        animation: ${popIn} 125ms ease;

        & > svg {
          margin-left: 8px;
        }
      `;
    }

    case 'greenBlueGradient': {
      return css`
        background: linear-gradient(
          90deg,
          rgba(210, 217, 67, 0.4) 0%,
          rgba(83, 193, 222, 0.4) 100%
        );
        color: ${theme.colorsV2.blue80};
      `;
    }

    case 'jobCard': {
      return css`
        background-color: ${theme.colorsV2.gray20};
        gap: 6px;

        & svg {
          color: ${theme.colorsV2.brandPurple};
          stroke-width: 1;
          stroke: ${theme.colorsV2.brandPurple};
        }
      `;
    }

    case 'outline': {
      return css`
        background-color: ${theme.colorsV2.white};
        color: ${theme.colorsV2.black};
        border: 1px solid ${theme.colorsV2.gray30};
        opacity: 1;
        transition: opacity 175ms ease;

        ${!$readOnly &&
        css`
          &:hover,
          &:focus,
          &:focus-within,
          &:active {
            cursor: ${cursor};
            opacity: 1;
            border: 1px solid ${theme.colorsV2.gray80};
          }
        `}
      `;
    }

    case 'pinkGradient': {
      return css`
        background: linear-gradient(
          90deg,
          rgba(243, 202, 77, 0.35) 0%,
          rgba(255, 152, 154, 0.35) 42.88%,
          rgba(203, 171, 255, 0.35) 94.62%
        );
        color: ${theme.colorsV2.red80};
      `;
    }

    case 'placeholder': {
      return css`
        ${dashedBorderBackground({
          radius: MOBILE_TAG_HEIGHT / 2,
          stroke: theme.colorsV2.gray60,
          strokeDashoffset: 2,
        })}

        ${theme.mediaQueries.lg} {
          ${dashedBorderBackground({
            radius: DESKTOP_TAG_HEIGHT / 2,
            stroke: theme.colorsV2.gray60,
            strokeDashoffset: 2,
          })}
        }
      `;
    }

    case 'purple': {
      return css`
        background-color: #ddd8ff;
        color: ${theme.colorsV2.brandPurple};
        border: 1px solid #ddd8ff;
        opacity: 1;
        transition: opacity 175ms ease;
        animation: ${popIn} 125ms ease;

        & > svg {
          margin-left: 8px;
        }
      `;
    }

    case 'purpleSemiBold': {
      return css`
        background-color: #ddd8ff;
        color: ${theme.colorsV2.brandPurple};
        border: 1px solid #ddd8ff;
        opacity: 1;
        transition: opacity 175ms ease;
        animation: ${popIn} 125ms ease;
        height: ${MOBILE_TAG_HEIGHT}px;
        border-radius: ${MOBILE_TAG_HEIGHT / 2}px;

        & > svg {
          margin-left: 8px;
        }
      `;
    }

    case 'search': {
      return css`
        background-color: ${theme.colorsV2.white};
        color: ${theme.colorsV2.black};
        border: 1px solid ${theme.colorsV2.gray40};
        opacity: 1;
        gap: 6px;

        ${!$readOnly &&
        css`
          &:hover,
          &:focus,
          &:focus-within,
          &:active {
            cursor: ${cursor};
            opacity: 1;
            border: 1px solid ${theme.colorsV2.gray80};
          }
        `}
      `;
    }

    case 'socialShare': {
      return css`
        background-color: ${theme.colorsV2.white};
        height: auto;
        border: 1px solid ${theme.colorsV2.gray30};
        padding: 8px 8px;

        ${theme.mediaQueries.lg} {
          height: auto;
        }
      `;
    }

    case 'suggestion': {
      return css`
        cursor: ${cursor};
        box-shadow: ${theme.boxShadow.light};
        opacity: 0.7;
        transition: opacity 175ms ease;
        animation: ${popIn} 125ms ease;

        & > svg {
          margin-left: 8px;
        }

        &:hover {
          opacity: 1;
        }
      `;
    }

    case 'white': {
      return css`
        background-color: ${theme.colorsV2.white};
      `;
    }

    case 'whiteBorder': {
      return css`
        background-color: ${theme.colorsV2.white};
        border: 1px solid ${theme.colorsV2.gray30};
      `;
    }

    case 'whiteMobileLarger': {
      return css`
        background-color: ${theme.colorsV2.white};
        height: ${MOBILE_TAG_HEIGHT_LARGE}px;
        border-radius: ${MOBILE_TAG_HEIGHT_LARGE / 2}px;
      `;
    }

    case 'yellow': {
      return css`
        background-color: ${theme.colorsV2.yellow05};
        color: ${theme.colorsV2.yellow80};
        opacity: 1;
        transition: opacity 175ms ease;
        animation: ${popIn} 125ms ease;

        & > svg {
          margin-left: 8px;
        }
      `;
    }

    case 'default':
    default: {
      return css`
        background-color: ${theme.colorsV2.white};
        border: 1px solid ${theme.colorsV2.gray30};
        padding: 8px 12px;

        &:focus {
          border-color: ${theme.colorsV2.gray05};
        }
      `;
    }
  }
};

const getTagSizeStyles = ({
  size,
  theme,
}: {
  size: TagProps['size'];
  theme: Theme;
}) => {
  switch (size) {
    case 'medium': {
      return css`
        height: ${MEDIUM_TAG_SIZE}px !important;
        border-radius: 44px;
      `;
    }

    case 'small': {
      return css`
        height: ${SMALL_TAG_SIZE}px !important;
        border-radius: 44px;
      `;
    }

    default: {
      return css`
        height: ${MOBILE_TAG_HEIGHT}px;
        border-radius: ${MOBILE_TAG_HEIGHT / 2}px;

        ${theme.mediaQueries.lg} {
          height: ${DESKTOP_TAG_HEIGHT}px;
          border-radius: ${DESKTOP_TAG_HEIGHT / 2}px;
        }
      `;
    }
  }
};

export const StyledTagBox = styled.div<{
  $hasIcon: boolean;
  $iconPlacement?: 'left' | 'right';
  $readOnly?: boolean;
  $showHoverState?: boolean;
  $size: TagProps['size'];
  name?: string;
  variant: TagVariant;
}>`
  ${({
    $hasIcon = false,
    $iconPlacement = 'left',
    $readOnly = false,
    $showHoverState = false,
    $size = 'default',
    theme,
    variant,
  }) => css`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    width: auto;
    white-space: nowrap;
    padding: 10px 12px;
    overflow: hidden;

    ${getTagVariantStyles(theme, variant, $readOnly, $showHoverState)}

    ${getTagSizeStyles({
      size: $size,
      theme,
    })}

    ${$hasIcon &&
    $iconPlacement === 'left' &&
    css`
      padding-left: 6px;
    `}

    ${$hasIcon &&
    $iconPlacement === 'right' &&
    css`
      padding-right: 6px;
    `}

    ${theme.mediaQueries.lg} {
      ${(variant === 'condensed' ||
        variant === 'condensed-link' ||
        variant === 'gray-condensed' ||
        variant === 'outline' ||
        variant === 'search' ||
        variant === 'dotted' ||
        variant === 'gray20' ||
        variant === 'gray10' ||
        variant === 'gray90' ||
        variant === 'jobCard' ||
        variant === 'purple' ||
        variant === 'purpleSemiBold' ||
        variant === 'blueSemiBold' ||
        variant === 'blue') &&
      css`
        height: ${MOBILE_TAG_HEIGHT}px;
        border-radius: ${MOBILE_TAG_HEIGHT / 2}px;
      `}

      ${variant === 'socialShare' && `height: auto`}
    }

    transition:
      border 150ms ease-out,
      background-color 150ms ease-out;
  `}
`;

const IconContainer = styled.div<{
  $size?: number | TagProps['size'];
  iconVariant: TagIconVariant;
  variant?: TagVariant;
}>`
  ${({ $size, iconVariant, theme, variant }) => {
    const mobileIconSize =
      typeof $size === 'number'
        ? $size
        : getMobileIconSize({
            size: $size,
            variant,
          });

    const desktopIconSize =
      typeof $size === 'number'
        ? $size
        : $size === 'small' || variant === 'search' || variant === 'outline'
          ? mobileIconSize
          : DESKTOP_ICON_SIZE;

    return css`
      display: flex;
      justify-content: center;
      align-items: center;
      flex-grow: 0;

      ${iconVariant === 'ROUNDED' ? `background: ${theme.colorsV2.gray05}` : ''}
      ${iconVariant === 'ROUNDED' || iconVariant === 'ROUNDED_NO_BACKGROUND'
        ? 'border-radius: 50%;'
        : ''}

      height: ${mobileIconSize}px;

      & > img {
        ${iconVariant === 'ROUNDED' || iconVariant === 'ROUNDED_NO_BACKGROUND'
          ? 'border-radius: 50%;'
          : ''}
        width: ${mobileIconSize}px;
        height: ${mobileIconSize}px;
      }

      & > svg {
        width: ${mobileIconSize / 1.5}px;
        height: ${mobileIconSize / 1.5}px;
      }

      ${theme.mediaQueries.lg} {
        width: ${desktopIconSize}px;
        height: ${desktopIconSize}px;

        & > svg {
          width: ${desktopIconSize}px;
          height: ${desktopIconSize}px;
        }

        & > img {
          ${iconVariant === 'ROUNDED' || iconVariant === 'ROUNDED_NO_BACKGROUND'
            ? 'border-radius: 50%;'
            : ''}
          width: ${desktopIconSize}px;
          height: ${desktopIconSize}px;
        }
      }
    `;
  }}
`;

const SortHandle = styled.div`
  ${({ theme }) => css`
    cursor: move;
    color: ${theme.colorsV2.gray70};
    touch-action: none;
  `}
`;

const StyledText = styled(Text)<{
  $size?: TagProps['size'];
  variant?: TagVariant;
}>`
  ${({ $size, theme, variant = 'default' }) => css`
    flex-grow: 1;
    text-align: center;
    font-size: ${theme.textStyles.captionSmall.fontSize};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    ${variant === 'whiteMobileLarger' &&
    css`
      font-size: ${theme.textStyles.bodySmall.fontSize};
      font-weight: ${theme.textStyles.bodySmall.fontWeight};
      letter-spacing: ${theme.textStyles.bodySmall.letterSpacing};
      line-height: ${theme.textStyles.bodySmall.lineHeight};
    `}

    ${(variant === 'purpleSemiBold' || variant === 'blueSemiBold') &&
    css`
      font-weight: ${theme.fontWeights.semiBold};
    `}

    ${theme.mediaQueries.lg} {
      font-size: ${theme.textStyles.bodySmall.fontSize};
      font-weight: ${theme.textStyles.bodySmall.fontWeight};
      letter-spacing: ${theme.textStyles.bodySmall.letterSpacing};
      line-height: ${theme.textStyles.bodySmall.lineHeight};

      ${$size === 'default' &&
      (variant === 'condensed' ||
        variant === 'condensed-link' ||
        variant === 'gray-condensed' ||
        variant === 'outline' ||
        variant === 'gray20' ||
        variant === 'gray90' ||
        variant === 'purple' ||
        variant === 'blue') &&
      css`
        font-size: ${theme.textStyles.captionSmall.fontSize};
        font-weight: ${theme.textStyles.captionSmall.fontWeight};
        letter-spacing: ${theme.textStyles.captionSmall.letterSpacing};
        line-height: ${theme.textStyles.captionSmall.lineHeight};
      `}

      ${variant === 'jobCard' &&
      css`
        font-size: 12px;
      `}

      ${(variant === 'outline' || variant === 'socialShare') &&
      css`
        font-size: ${theme.textStyles.captionRegular.fontSize};
        font-weight: ${theme.textStyles.captionRegular.fontWeight};
        letter-spacing: ${theme.textStyles.captionRegular.letterSpacing};
        line-height: ${theme.textStyles.captionRegular.lineHeight};
      `}

      ${variant === 'search' &&
      css`
        font-size: 14px;
        font-weight: ${theme.textStyles.captionRegular.fontWeight};
        letter-spacing: ${theme.textStyles.captionRegular.letterSpacing};
        line-height: ${theme.textStyles.captionRegular.lineHeight};
      `}

      ${variant === 'pinkGradient' &&
      css`
        font-size: 10px;
        font-weight: 600;
      `}

      ${variant === 'greenBlueGradient' &&
      css`
        font-size: 10px;
        font-weight: 600;
      `}
    }

    ${$size === 'small' &&
    css`
      font-size: 0.875rem;
      letter-spacing: 0.15px;
      line-height: 1.42;
    `}
  `}
`;

const FocusIconButton = styled.button`
  ${({ theme }) => css`
    display: flex;
    touch-action: none;
    & > svg {
      color: ${theme.colorsV2.gray60};
    }

    &:focus {
      outline: 1px solid ${theme.colorsV2.gray60};
    }
  `}
`;

export type TagIconVariant =
  | 'ROUNDED'
  | 'ROUNDED_NO_BACKGROUND'
  | 'SQUARE_NO_BACKGROUND';

export type TagMode = 'add' | 'checked' | 'none' | 'remove' | 'select';

export type TagProps = Sortable & {
  readonly ariaLabel?: string;
  readonly className?: string;
  readonly href?: string;
  readonly icon?: JSX.Element | { uid: string };
  readonly iconPlacement?: 'left' | 'right';
  readonly iconSize?: number;
  readonly iconVariant?: TagIconVariant;
  readonly isDraggable?: boolean;
  readonly label: string;
  readonly mode?: TagMode;
  readonly name?: string;
  readonly onClick?: (
    value: string,
    event?:
      | KeyboardEvent<HTMLButtonElement>
      | KeyboardEvent<HTMLDivElement>
      | MouseEvent<HTMLButtonElement>
      | MouseEvent<HTMLDivElement>,
  ) => void;
  readonly onRemove?: () => void;
  readonly readOnly?: boolean;
  readonly showHoverState?: boolean;
  readonly size?: TagSize;
  readonly tabIndex?: number;
  readonly tooltip?: string;
  readonly value?: string;
  readonly variant?: TagVariant;
};

export type TagSize = 'default' | 'medium' | 'small';

export type TagVariant =
  | 'blue'
  | 'blueSemiBold'
  | 'condensed'
  | 'condensed-link'
  | 'default'
  | 'dotted'
  | 'ghost'
  | 'gray10'
  | 'gray20'
  | 'gray90'
  | 'gray'
  | 'gray-condensed'
  | 'green'
  | 'greenBlueGradient'
  | 'jobCard'
  | 'outline'
  | 'pinkGradient'
  | 'placeholder'
  | 'purple'
  | 'purpleSemiBold'
  | 'search'
  | 'socialShare'
  | 'suggestion'
  | 'white'
  | 'whiteBorder'
  | 'whiteMobileLarger'
  | 'yellow';

const DefaultContentContainer = styled.div`
  display: flex;
  gap: 4px;
  justify-content: center;
  align-items: center;
`;

const DefaultContent = ({
  icon,
  iconPlacement,
  iconSize,
  iconVariant = 'ROUNDED',
  isDraggable,
  label,
  mode = 'remove',
  onClick,
  onRemove,
  size,
  sortableListeners,
  value,
  variant,
}: Pick<
  TagProps,
  | 'icon'
  | 'iconPlacement'
  | 'iconSize'
  | 'iconVariant'
  | 'isDraggable'
  | 'label'
  | 'mode'
  | 'onClick'
  | 'onRemove'
  | 'size'
  | 'sortableListeners'
  | 'value'
  | 'variant'
>) => {
  const iconElement = getIconElement({ icon, variant });
  const ActionIcon = getTagActionIcon(mode);

  return (
    <DefaultContentContainer>
      {isDraggable && (
        <SortHandle
          role="button"
          tabIndex={0}
          {...sortableListeners}
        >
          <MoveIcon size={20} />
        </SortHandle>
      )}
      {iconElement && iconPlacement === 'left' ? (
        <IconContainer
          $size={iconSize ?? size}
          aria-hidden
          iconVariant={iconVariant}
          role="img"
          // passing variant in here introduces visual regressions, so we only apply it for this specific variant where it's needed
          variant={
            variant === 'whiteMobileLarger' ? 'whiteMobileLarger' : undefined
          }
        >
          {iconElement}
        </IconContainer>
      ) : null}
      <StyledText
        as="span"
        textStyle="captionSmall"
        variant={variant}
      >
        {label}
      </StyledText>
      {iconElement && iconPlacement === 'right' ? (
        <IconContainer
          $size={iconSize ?? size}
          aria-hidden
          iconVariant={iconVariant}
          role="img"
          // passing variant in here introduces visual regressions, so we only apply it for this specific variant where it's needed
          variant={
            variant === 'whiteMobileLarger' ? 'whiteMobileLarger' : undefined
          }
        >
          {iconElement}
        </IconContainer>
      ) : null}
      {onClick && (
        <FocusIconButton
          aria-label="Remove"
          onClick={(event: MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();
            if (value) {
              onClick(value, event);
            }
          }}
          onKeyDown={(event: KeyboardEvent<HTMLButtonElement>) => {
            event.stopPropagation();
            if (value && event.key === ' ') {
              event.preventDefault();
              onClick(value, event);
            }
          }}
          role="button"
          type="button"
        >
          {ActionIcon && (
            <ActionIcon
              aria-hidden
              onClick={
                mode === 'remove' && onRemove
                  ? (event) => {
                      event.stopPropagation();
                      onRemove();
                    }
                  : undefined
              }
              size={16}
            />
          )}
        </FocusIconButton>
      )}
    </DefaultContentContainer>
  );
};

// @todo: fix color issue on SVGs by defining unique template
export const Tag = forwardRef(
  (
    {
      ariaLabel,
      className,
      href,
      icon,
      iconPlacement = 'left',
      iconSize,
      iconVariant = 'ROUNDED',
      isDraggable = false,
      label,
      mode = 'add',
      name,
      onClick,
      onRemove,
      readOnly = false,
      showHoverState = false,
      size,
      sortableListeners,
      tabIndex,
      tooltip,
      value,
      variant = 'default',
    }: TagProps,
    ref: Ref<HTMLDivElement>,
  ) => {
    const derivedVariant = variant ?? 'condensed-link';
    const displayTagIcon = variant !== 'dotted';
    const iconElement = getIconElement({ icon, variant });

    if (href) {
      const content = (
        <>
          {iconElement && displayTagIcon && iconPlacement === 'left' ? (
            <IconContainer
              $size={iconSize ?? size}
              aria-hidden
              iconVariant={iconVariant}
              role="img"
            >
              {iconElement}
            </IconContainer>
          ) : null}
          <StyledText
            as="span"
            textStyle="captionSmall"
            variant={derivedVariant}
          >
            {label}
          </StyledText>
          {iconElement && displayTagIcon && iconPlacement === 'right' ? (
            <IconContainer
              $size={iconSize ?? size}
              aria-hidden
              iconVariant={iconVariant}
              role="img"
            >
              {iconElement}
            </IconContainer>
          ) : null}
        </>
      );
      return (
        <StyledTagBox
          $hasIcon={Boolean(icon)}
          $iconPlacement={iconPlacement}
          $readOnly={readOnly}
          $showHoverState={showHoverState}
          $size={size ?? 'default'}
          aria-label={ariaLabel ?? label}
          as="a"
          className={className}
          href={href}
          name={name}
          variant={derivedVariant}
        >
          {tooltip ? (
            <Tooltip
              content={tooltip}
              variant="white"
            >
              {content}
            </Tooltip>
          ) : (
            content
          )}
        </StyledTagBox>
      );
    }

    if (
      (variant === 'suggestion' ||
        variant === 'outline' ||
        variant === 'search' ||
        variant === 'dotted' ||
        variant === 'blue' ||
        variant === 'pinkGradient' ||
        variant === 'greenBlueGradient' ||
        variant === 'jobCard') &&
      onClick
    ) {
      const ActionIcon = getTagActionIcon(mode);
      const content = (
        <>
          {iconElement && iconPlacement === 'left' ? (
            <IconContainer
              aria-hidden
              iconVariant={iconVariant}
              role="img"
              variant={variant}
            >
              {iconElement}
            </IconContainer>
          ) : null}
          <StyledText
            as="span"
            textStyle="captionSmall"
            variant={variant}
          >
            {label}
          </StyledText>
          {iconElement && iconPlacement === 'right' ? (
            <IconContainer
              aria-hidden
              iconVariant={iconVariant}
              role="img"
              variant={variant}
            >
              {iconElement}
            </IconContainer>
          ) : null}
          {displayTagIcon && ActionIcon && (
            <ActionIcon
              aria-hidden
              size={mode === 'remove' ? 14 : 16}
            />
          )}
        </>
      );
      return (
        <StyledTagBox
          $hasIcon={Boolean(icon)}
          $iconPlacement={iconPlacement}
          $readOnly={readOnly}
          $showHoverState={showHoverState}
          $size={size ?? 'default'}
          aria-label={
            ariaLabel ?? `${mode === 'add' ? 'Add' : 'Remove'} ${label}`
          }
          className={className}
          name={name}
          onClick={(event: MouseEvent<HTMLDivElement>) => {
            event.stopPropagation();
            if (value) onClick(value, event);
          }}
          onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
            event.stopPropagation();
            if (value && event.key === ' ') onClick(value, event);
          }}
          ref={ref}
          role="button"
          tabIndex={tabIndex}
          variant={variant}
        >
          {tooltip ? (
            <Tooltip
              content={tooltip}
              variant="white"
            >
              {content}
            </Tooltip>
          ) : (
            content
          )}
        </StyledTagBox>
      );
    }

    const defaultContent = (
      <DefaultContent
        icon={icon}
        iconPlacement={iconPlacement}
        iconSize={iconSize}
        iconVariant={iconVariant}
        isDraggable={isDraggable}
        label={label}
        mode={mode}
        onClick={onClick}
        onRemove={onRemove}
        size={size}
        sortableListeners={sortableListeners}
        value={value}
        variant={variant}
      />
    );

    return (
      <StyledTagBox
        $hasIcon={Boolean(icon)}
        $iconPlacement={iconPlacement}
        $readOnly={readOnly}
        $showHoverState={showHoverState}
        $size={size ?? 'default'}
        aria-label={
          ariaLabel ??
          `${mode === 'add' || mode === 'none' ? 'Add' : 'Remove'} ${label}`
        }
        className={className}
        name={name}
        onClick={(event: MouseEvent<HTMLDivElement>) => {
          if (onClick) {
            onClick(value ?? '', event);
          }
        }}
        ref={ref}
        variant={variant}
      >
        {tooltip ? (
          <Tooltip
            content={tooltip}
            variant="white"
          >
            {defaultContent}
          </Tooltip>
        ) : (
          defaultContent
        )}
      </StyledTagBox>
    );
  },
);
